/**
 * Hotspot preview harness.
 *
 * Builds the document that goes into the Hotspot layout's <iframe srcDoc>: one of
 * CD02 Module 6's replica screens, wrapped by an injected prelude and runner that
 * turn every `[data-hs]` region into an answerable target and report clicks back to
 * the parent.
 *
 * ── How this differs from webLabHarness ───────────────────────────────────────
 * Web Lab renders code the STUDENT wrote and asserts against it once, one-way
 * (frame → parent). Here the document is authored by us and never edited; the
 * student answers questions by clicking it. Two consequences:
 *
 *   1. Messaging is BIDIRECTIONAL. The parent owns the question order and the
 *      score, but it cannot style across the sandbox boundary — so it sends
 *      { type: "hotspot:mark", id, state, label } and the runner in here applies
 *      the class and hangs the label chip. Only an id, a state word and authored
 *      label copy ever cross; no student data and no secrets.
 *
 *   2. There is NO localStorage shim. These screens save nothing, so the reason
 *      Web Lab needs one (an origin-null document throws on localStorage) never
 *      arises. That shim was most of webLabHarness; this file is smaller for it.
 *
 * The sandbox itself is unchanged and non-negotiable: allow-scripts WITHOUT
 * allow-same-origin. V2605's replica interpolates {{APP_NAME}} — text the student
 * typed into their project — so this document is not trustworthy markup even though
 * we authored the frame around it. Granting both flags together would make it
 * same-origin with the app and let it read the parent's localStorage, where Firebase
 * keeps auth tokens. See HotspotLayout.js and webLabHarness.js.
 *
 * Because the origin is "null", the parent cannot authenticate messages by
 * event.origin. Both directions match on the per-run nonce, and the parent also
 * matches on event.source === frame.contentWindow.
 *
 * ── Why this is injected in two pieces ────────────────────────────────────────
 * The RUNNER must run last: it queries [data-hs], which only exists once the body
 * is parsed. But the PRELUDE has to run first, and not only out of symmetry with
 * Web Lab — on a screen with `revealDesc`, the menu descriptions are the answers,
 * and veiling them from a script at the end of <body> would show them unblurred for
 * a frame first. So the prelude marks <html> before the body paints and lets CSS do
 * the hiding, which cannot flash.
 */

// The exercise chrome — outlines, the blur veil, the answer chips. Injected from
// here rather than added to claudeCodeScreen's own <style> so the replicas stay
// pure: a screen is a picture of the app and knows nothing about being quizzed.
// Every class is namespaced `hs-` so it cannot collide with the replica's own.
const HOTSPOT_CSS = `
  /* These screens are PICTURES TO CLICK, not text to read out or copy, and letting
     them be selected broke the exercise outright.

     Drag across a region — which V2512's "click what you would send them" invited,
     because selecting a URL is how you send one — and Chrome treats it as a text
     selection and SUPPRESSES the click entirely. Not a wrong answer: no event at
     all. The student presses, drags, releases, and the lesson does not react, which
     reads as broken rather than as a miss. Turning selection off restores the click
     (it lands on the common ancestor of press and release), and the runner's
     mousedown fallback then resolves it back to the region they started on. */
  body {
    -webkit-user-select: none;
    user-select: none;
  }

  [data-hs] {
    cursor: pointer;
    border-radius: 6px;
    outline: 1px dashed transparent;
    outline-offset: 2px;
    transition: outline-color .12s ease, background-color .12s ease;
  }
  [data-hs]:hover, [data-hs]:focus-visible {
    outline-color: #6f6c66;
  }
  [data-hs]:focus-visible {
    outline: 2px solid #d97757;
  }

  /* Answered correctly — stays marked for the rest of the lesson, so the screen
     ends up fully annotated and readable as a reference. */
  [data-hs].hs-found {
    outline: 1px solid #6ee7a0;
    background: rgba(110, 231, 160, .09);
    cursor: default;
  }
  .hs-chip {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 7px;
    border-radius: 5px;
    background: rgba(110, 231, 160, .16);
    color: #6ee7a0;
    font-size: 11px;
    line-height: 1.5;
    vertical-align: middle;
  }

  /* Wrong answer — one flash, then it clears itself. Nothing is disabled and
     nothing is scored down; this is a nudge, not a penalty. */
  [data-hs].hs-miss {
    outline: 1px solid #e08a7a;
    background: rgba(224, 138, 122, .12);
    animation: hs-shake .3s ease;
  }
  @keyframes hs-shake {
    0%, 100% { transform: translateX(0); }
    25%      { transform: translateX(-3px); }
    75%      { transform: translateX(3px); }
  }

  /* revealDesc screens: the menu descriptions state the answers in words, so they
     start hidden and each is uncovered as its row is found. Driven by a class on
     <html> that the prelude sets BEFORE the body paints — a JS pass at the end of
     <body> would show them unblurred for one frame, which gives the answer away. */
  .hs-veiled .mdesc {
    filter: blur(5px);
  }
  .hs-veiled [data-hs].hs-found .mdesc {
    filter: none;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-hs].hs-miss { animation: none; }
    [data-hs] { transition: none; }
  }
`;

// Runs FIRST, before the body is parsed.
const PRELUDE_SOURCE = `
(function () {
  var NONCE = "__NONCE__";
  var OPTS = __OPTS__;

  function post(msg) {
    try {
      msg.nonce = NONCE;
      parent.postMessage(msg, "*");
    } catch (e) { /* nothing we can do from in here */ }
  }
  window.__hotspotPost = post;

  var style = document.createElement("style");
  style.textContent = __CSS__;
  (document.head || document.documentElement).appendChild(style);

  // Set before anything paints, so the veil can never flash open.
  if (OPTS.revealDesc) document.documentElement.className += " hs-veiled";

  window.addEventListener("error", function (e) {
    post({ type: "hotspot:error", message: String((e && e.message) || e) });
  });
})();
`;

// Runs LAST, once the replica's markup is parsed and [data-hs] can be queried.
const RUNNER_SOURCE = `
(function () {
  var NONCE = "__NONCE__";

  var post = window.__hotspotPost || function (msg) {
    try { msg.nonce = NONCE; parent.postMessage(msg, "*"); } catch (e) {}
  };

  var spots = [].slice.call(document.querySelectorAll("[data-hs]"));

  // Reachable without a mouse. ConceptCanvas learned this the hard way for drag
  // (see its header): a pointer-only interaction is not an interaction for
  // everyone. Enter and Space answer, exactly like the click.
  spots.forEach(function (el) {
    if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "0");
    if (!el.hasAttribute("role")) el.setAttribute("role", "button");
  });

  // What the mouse was pressed DOWN on. A drag fires its click on the common
  // ancestor of press and release, so releasing a couple of pixels outside the
  // region you meant reports dead space — a wrong answer for a gesture that was
  // aimed correctly. The press is the intent; the release is just where the hand
  // stopped. Cleared after each answer so it can never leak into a later click.
  var pressed = null;
  document.addEventListener("mousedown", function (e) {
    pressed = e.target && e.target.closest ? e.target.closest("[data-hs]") : null;
  });

  function answer(from) {
    var spot = (from && from.closest ? from.closest("[data-hs]") : null) || pressed;
    pressed = null;
    // id: null means the student clicked dead space. Reported rather than
    // swallowed, so the parent can say "nothing to identify there" instead of
    // leaving them wondering whether the click registered at all.
    post({ type: "hotspot:hit", id: spot ? spot.getAttribute("data-hs") : null });
  }

  document.addEventListener("click", function (e) {
    // The replicas contain real <button>s (Send, Accept, Create PR). They have no
    // handlers on a finished screen, but stop them anyway so a stray submit or
    // focus scroll can never look like part of the exercise.
    e.preventDefault();
    answer(e.target);
  });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Enter" && e.key !== " " && e.key !== "Spacebar") return;
    var spot = e.target && e.target.closest ? e.target.closest("[data-hs]") : null;
    if (!spot) return;
    e.preventDefault();
    answer(spot);
  });

  // The parent owns the question order and the score; this only paints what it is
  // told to. Marking is idempotent so a replayed message cannot stack two chips.
  window.addEventListener("message", function (e) {
    if (e.source !== parent) return;
    var d = e.data;
    if (!d || typeof d !== "object" || d.nonce !== NONCE) return;
    if (d.type !== "hotspot:mark") return;

    // There is deliberately no "clear" state. Start over remounts the iframe with a
    // fresh nonce, so the document is rebuilt from scratch — un-marking every region
    // and re-applying the veil for free. A reset path in here would be a second way
    // to do the same thing, and the one that could drift.

    // Every question answered. Uncover the descriptions the student never had to
    // click — the distractor rows — so the finished screen reads as a complete
    // reference. Without this, half of a menu stays blurred forever, because only a
    // row that gets FOUND ever has its own veil lifted.
    if (d.state === "reveal") {
      document.documentElement.classList.remove("hs-veiled");
      return;
    }

    var el = document.querySelector('[data-hs="' + String(d.id).replace(/"/g, "") + '"]');
    if (!el) return;

    if (d.state === "found") {
      el.classList.remove("hs-miss");
      el.classList.add("hs-found");
      el.removeAttribute("tabindex");
      if (d.label && !el.querySelector(":scope > .hs-chip")) {
        var chip = document.createElement("span");
        chip.className = "hs-chip";
        chip.textContent = String(d.label);
        el.appendChild(chip);
      }
      return;
    }

    if (d.state === "miss") {
      el.classList.add("hs-miss");
      setTimeout(function () { el.classList.remove("hs-miss"); }, 400);
    }
  });

  // Hands the parent the ids that actually exist on this screen. authoredLessons
  // .test.js already checks every target resolves, but that only guards the
  // authored pair — this catches a screen whose markup changed underneath it at
  // runtime, and lets the layout warn in development instead of presenting a
  // question with no answer.
  post({
    type: "hotspot:ready",
    ids: spots.map(function (el) { return el.getAttribute("data-hs"); }),
  });
})();
`;

// Replacer FUNCTIONS, never plain strings: `String.replace` treats `$&`, `` $` ``,
// `$'` and `$$` as special in a string replacement, and authored label copy is free
// text that can easily contain them. Getting this wrong corrupts the injected
// script rather than just the visible text.
const fill = (source, values) =>
    Object.keys(values).reduce(
        (acc, token) => acc.replace(token, () => values[token]),
        source
    );

/**
 * Compose the srcDoc for a hotspot screen.
 *
 * @param {string} html    The authored replica document.
 * @param {object} options { revealDesc?: boolean } — see HOTSPOT_CSS.
 * @param {string} nonce   Per-run token; both sides ignore messages without it.
 * @returns {string}
 */
export const buildHotspotSrcDoc = (html, options, nonce) => {
    const prelude = `<script>${fill(PRELUDE_SOURCE, {
        __NONCE__: String(nonce),
        __OPTS__: JSON.stringify({ revealDesc: !!(options && options.revealDesc) }),
        __CSS__: JSON.stringify(HOTSPOT_CSS),
    })}</script>`;

    const runner = `<script>${fill(RUNNER_SOURCE, { __NONCE__: String(nonce) })}</script>`;

    let doc = typeof html === "string" ? html : "";

    // Prelude as early as the document allows — after <head>, else after the <body>
    // opening tag, else at the very top. Never before <html>, which would be
    // invalid. Same ladder as webLabHarness; the replicas are always full
    // documents, but a fragment must still not break the page.
    if (/<head[^>]*>/i.test(doc)) {
        doc = doc.replace(/<head[^>]*>/i, (m) => `${m}${prelude}`);
    } else if (/<body[^>]*>/i.test(doc)) {
        doc = doc.replace(/<body[^>]*>/i, (m) => `${m}${prelude}`);
    } else {
        doc = prelude + doc;
    }

    // Runner last, so [data-hs] is queryable.
    if (/<\/body\s*>/i.test(doc)) {
        return doc.replace(/<\/body\s*>/i, () => `${runner}</body>`);
    }
    return doc + runner;
};

export default buildHotspotSrcDoc;
